import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ROUTE_TYPES } from "../../classes/constants/routeConstants";
import { useAppContext } from "../../classes/contexts/AppContext";
import { useRemoteRenderContext } from "../../classes/contexts/RemoteRenderContext";
import { vertebraGroups } from "../../classes/enums/AnnotationsEnum";
import { EventsEnum } from "../../classes/enums/EventsEnum";
import {
  MedicalImagesType,
  SPINESIM_MODALITIES,
} from "../../classes/enums/MedicalImagesEnum";
import { PLANNING_STATES } from "../../classes/enums/PlanningStateEnum";
import { SidebarSize } from "../../classes/enums/SideBarsEnum";
import { MacroRegions } from "../../classes/enums/ValidationEnums";
import { vertebraUtils } from "../../classes/utils/AnnotationsUtils";
import { generateCacheKey } from "../../classes/utils/RemoteRenderingUtils";
import { getGroupedThumbnails } from "../../classes/utils/Thumbnailsutils";
import ImageViewer from "../../components/imageViewer/ImageViewer";
import RemoteViewer from "../../components/remoteViewer/RemoteViewer";
import ThumbnailTab from "../../components/thumbnailTab/ThumbnailTab";
import ValidationPointLabel from "../../components/validationPointLabel/ValidationPointLabel";
import ViewLoader from "../../components/viewLoader/ViewLoader";
import { usePatientInstancesByModality } from "../../hooks/queries/usePatientInstancesByModality";
import { useDataBundle } from "../../hooks/useDigitalTwinDataBundle";
import { useRouteState } from "../../hooks/useRouteState";
import { rerunInstanceLandmarking } from "../../services/InstancesService";
import "../common/stepInteractionRegion/VisualizationArea.css";
import ToolBar from "../common/toolBar/ToolBar";
import ValidationToolsWidget from "../validation/validationToolsWidget/ValidationToolsWidget";
import "./Validation.css";
import ValidationOverlayTools from "./validationOverlay/ValidationOverlayTools";

// Normalize view name to display settings key
const getDisplaySettingsViewType = viewName => {
  if (!viewName) return null;
  const lower = viewName.toLowerCase();
  if (lower.includes("lateral") || lower.includes("sagittal")) return "lateral";
  if (lower.includes("front") || lower.includes("coronal")) return "frontal";
  if (lower.includes("axial")) return "axial";
  return null;
};

const Validation = () => {
  const routeState = useRouteState();
  const validationMode = routeState.getMode() || null;
  const isDxMode = validationMode === SPINESIM_MODALITIES.DX;

  const {
    dataBundle,
    constructedImageData,
    instances,
    selectedTwin,
    validationRefresh,
    triggerCheckReload,
  } = useDataBundle();

  const {
    client: renderClient,
    digitalTwinSurgeryCache,
    surgerySessionVersions,
  } = useRemoteRenderContext();

  const validationSessionVersion =
    surgerySessionVersions?.[PLANNING_STATES.VALIDATE] ?? 0;

  const { instancesByModality } = usePatientInstancesByModality();
  const { showOnlyRightSidebar } = useAppContext();

  const [selectedModality, setSelectedModality] = useState(null);
  const [currentValidationRegion, setCurrentValidationRegion] = useState(
    MacroRegions.SPINE
  );
  const isOverlayVisible = useMemo(() => {
    return (
      currentValidationRegion === MacroRegions.SPINE ||
      currentValidationRegion === MacroRegions.ENDPLATES
    );
  }, [currentValidationRegion]);

  const [validationPoints, setValidationPoints] = useState([]);

  // Check if all DRR validation points have assigned centroids
  const allDrrLabelsAssigned = useMemo(() => {
    if (!isDxMode && validationPoints.length > 0) {
      // Get unique point indices
      const uniquePointIndices = [
        ...new Set(validationPoints.map(p => p.pointIndex)),
      ];
      // Check if all have uniqueId assigned
      return uniquePointIndices.every(pointIndex => {
        const point = validationPoints.find(p => p.pointIndex === pointIndex);
        return point && point.uniqueId !== null && point.uniqueId !== undefined;
      });
    }
    return true; // For DX mode, always return true
  }, [validationPoints, isDxMode]);

  const twinId = selectedTwin?.[0]?._id || "";

  const hasValidAnnotations = useMemo(() => {
    if (!constructedImageData) return false;
    return Object.values(constructedImageData).some(
      data => data.annotations && data.annotations.length > 0
    );
  }, [constructedImageData]);

  // Explicit per-modality prerequisite to load validation views
  const canLoadValidationViews = useMemo(() => {
    switch (validationMode) {
      case SPINESIM_MODALITIES.DX:
        // DX validation requires annotations — landmarks must exist on DX images
        return hasValidAnnotations;
      case SPINESIM_MODALITIES.DRR:
        // DRR validation reads DRR files from disk; annotations are the OUTPUT of
        // validation, not a prerequisite — always attempt to load
        return true;
      default:
        return hasValidAnnotations;
    }
  }, [validationMode, hasValidAnnotations]);

  const [isLoading, setIsLoading] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);
  const [areViewsReady, setAreViewsReady] = useState(false);
  const [rerunProgress, setRerunProgress] = useState({
    completed: 0,
    total: 0,
  });

  // Ref to prevent effect from running during rerun orchestration
  const isRerunOrchestrating = useRef(false);

  const groupedThumbnailsStandard = useMemo(() => {
    if (!instances || !instancesByModality) return [];

    const normalizedInstances = {
      ...instances,
      sagittal_dx_instance_id:
        instances.sagittal_dx_instance_id?._id ||
        instances.sagittal_dx_instance_id,
      coronal_dx_instance_id:
        instances.coronal_dx_instance_id?._id ||
        instances.coronal_dx_instance_id,
      ct_instance_id: instances.ct_instance_id?._id || instances.ct_instance_id,
      mri_instance_id:
        instances.mri_instance_id?._id || instances.mri_instance_id,
    };

    const allGrouped =
      getGroupedThumbnails(
        normalizedInstances,
        instancesByModality,
        null,
        false
      ) || [];

    if (validationMode === SPINESIM_MODALITIES.DX) {
      return allGrouped.filter(
        group => group.modality === SPINESIM_MODALITIES.DX
      );
    } else if (validationMode === SPINESIM_MODALITIES.DRR) {
      return allGrouped.filter(
        group => group.modality === SPINESIM_MODALITIES.CT
      );
    }

    return allGrouped;
  }, [instances, instancesByModality, validationMode]);

  const allInstances = useMemo(() => {
    if (!groupedThumbnailsStandard) return [];

    const instancesList = [];
    const instancesSet = new Set();

    groupedThumbnailsStandard.forEach(modalityGroup => {
      modalityGroup.instances.forEach(instance => {
        const instanceId = instance._id || instance.id;
        if (!instancesSet.has(instanceId)) {
          instancesSet.add(instanceId);
          instancesList.push(instance);
        }
      });
    });

    return instancesList;
  }, [groupedThumbnailsStandard]);

  const selectedTwinName = useMemo(() => {
    if (!selectedTwin) return "Loading...";
    return selectedTwin[0]?.title || "No Name";
  }, [selectedTwin]);

  const centroidOptions = useMemo(() => {
    const allVertebrae = [
      ...vertebraGroups.CERVICAL,
      ...vertebraGroups.THORACIC,
      ...vertebraGroups.LUMBAR,
    ];

    return allVertebrae.map(vertebra => ({
      value: vertebraUtils.getCentroidEntity(vertebra),
      label: vertebraUtils.getVertebraLabel(vertebra),
      vertebra,
    }));
  }, []);

  // Helper to get the next vertebra in anatomical order (top to bottom: C7→T1→...→L5)
  const getNextVertebraInOrder = useCallback(
    currentVertebraId => {
      const currentIndex = centroidOptions.findIndex(
        opt => opt.value === currentVertebraId
      );
      if (currentIndex === -1 || currentIndex >= centroidOptions.length - 1) {
        return null;
      }
      return centroidOptions[currentIndex + 1].value;
    },
    [centroidOptions]
  );

  // Helper to get the previous vertebra in anatomical order
  const getPreviousVertebraInOrder = useCallback(
    currentVertebraId => {
      const currentIndex = centroidOptions.findIndex(
        opt => opt.value === currentVertebraId
      );
      if (currentIndex <= 0) {
        return null;
      }
      return centroidOptions[currentIndex - 1].value;
    },
    [centroidOptions]
  );

  useEffect(() => {
    // Skip effect if we're in the middle of rerun orchestration
    if (isRerunOrchestrating.current) {
      return;
    }

    const hasCorrectCachedViews = allInstances.some(instance => {
      const cacheKey = generateCacheKey(
        instance,
        twinId,
        PLANNING_STATES.VALIDATE
      );
      if (!digitalTwinSurgeryCache.hasView(cacheKey)) return false;

      const cachedData = digitalTwinSurgeryCache.getView(cacheKey);
      const views = Array.isArray(cachedData) ? cachedData : [cachedData];

      return views.some(
        view =>
          view.viewName === MedicalImagesType.LATERAL_VALIDATION_DX ||
          view.viewName === MedicalImagesType.FRONT_VALIDATION_DX ||
          view.viewName === MedicalImagesType.SAGITTAL_DRR_VALIDATION ||
          view.viewName === MedicalImagesType.CORONAL_DRR_VALIDATION
      );
    });

    const shouldLoadSurgery =
      canLoadValidationViews &&
      renderClient &&
      allInstances.length > 0 &&
      instances &&
      !hasCorrectCachedViews &&
      !isLoading; // Prevent parallel loads

    if (shouldLoadSurgery) {
      setIsLoading(true);

      (async () => {
        try {
          const response = await renderClient.LoadSpinesimViewStage(
            PLANNING_STATES.VALIDATE,
            JSON.stringify(constructedImageData),
            PLANNING_STATES.VALIDATE,
            true
          );

          const surgeryResult = JSON.parse(response);

          const sagittalInstanceId =
            instances.sagittal_dx_instance_id?._id ||
            instances.sagittal_dx_instance_id;
          const coronalInstanceId =
            instances.coronal_dx_instance_id?._id ||
            instances.coronal_dx_instance_id;
          const ctInstanceId =
            instances.ct_instance_id?._id || instances.ct_instance_id;
          const mriInstanceId =
            instances.mri_instance_id?._id || instances.mri_instance_id;

          const dxViews = surgeryResult.filter(
            view =>
              view.viewName === MedicalImagesType.LATERAL_VALIDATION_DX ||
              view.viewName === MedicalImagesType.FRONT_VALIDATION_DX
          );
          const ctViews = surgeryResult.filter(
            view =>
              view.viewName === MedicalImagesType.SAGITTAL_DRR_VALIDATION ||
              view.viewName === MedicalImagesType.CORONAL_DRR_VALIDATION
          );
          const mriViews = surgeryResult.filter(
            view =>
              (view.viewName === MedicalImagesType.SAGITTAL_MRI ||
                view.viewName === MedicalImagesType.CORONAL_MRI ||
                view.viewName === MedicalImagesType.AXIAL_MRI) &&
              view.modalityType === "MRI"
          );

          if (dxViews.length > 0) {
            const sagittalInstance = allInstances.find(
              inst => inst._id === sagittalInstanceId
            );
            const coronalInstance = allInstances.find(
              inst => inst._id === coronalInstanceId
            );

            if (sagittalInstance) {
              const sagittalCacheKey = generateCacheKey(
                sagittalInstance,
                twinId,
                PLANNING_STATES.VALIDATE
              );
              const sagittalViews = dxViews.filter(view =>
                view.viewName.includes("Lateral")
              );

              if (sagittalViews.length > 0) {
                digitalTwinSurgeryCache.setView(
                  sagittalCacheKey,
                  sagittalViews
                );
              }
            }

            if (coronalInstance) {
              const coronalCacheKey = generateCacheKey(
                coronalInstance,
                twinId,
                PLANNING_STATES.VALIDATE
              );
              const coronalViews = dxViews.filter(view =>
                view.viewName.includes("Front")
              );

              if (coronalViews.length > 0) {
                digitalTwinSurgeryCache.setView(coronalCacheKey, coronalViews);
              }
            }
          }

          if (ctViews.length > 0 && ctInstanceId) {
            const ctInstance = allInstances.find(
              inst => inst._id === ctInstanceId
            );

            if (ctInstance) {
              const ctCacheKey = generateCacheKey(
                ctInstance,
                twinId,
                PLANNING_STATES.VALIDATE
              );
              digitalTwinSurgeryCache.setView(ctCacheKey, ctViews);
            }
          }

          if (mriViews.length > 0 && mriInstanceId) {
            const mriInstance = allInstances.find(
              inst => inst._id === mriInstanceId
            );
            if (mriInstance) {
              const mriCacheKey = generateCacheKey(
                mriInstance,
                twinId,
                PLANNING_STATES.VALIDATE
              );
              digitalTwinSurgeryCache.setView(mriCacheKey, mriViews);
            }
          }

          setAreViewsReady(true);
        } catch (error) {
          // Error is logged, but not to console
        } finally {
          setIsLoading(false);
        }
      })();
    } else if (hasCorrectCachedViews) {
      setAreViewsReady(true);
    }
  }, [
    constructedImageData,
    canLoadValidationViews,
    renderClient,
    allInstances,
    instances,
    digitalTwinSurgeryCache,
    setAreViewsReady,
    twinId,
    validationSessionVersion,
    isLoading,
  ]);

  const currentModalityViews = useMemo(() => {
    if (!selectedTwin || !selectedModality || !areViewsReady) {
      return [];
    }

    const views = [];
    const modalityData = groupedThumbnailsStandard.find(
      item => item.modality === selectedModality
    );

    if (!modalityData) {
      return [];
    }

    const allowedViews = {
      DX: [
        MedicalImagesType.LATERAL_VALIDATION_DX,
        MedicalImagesType.FRONT_VALIDATION_DX,
      ],
      CT: [
        MedicalImagesType.SAGITTAL_DRR_VALIDATION,
        MedicalImagesType.CORONAL_DRR_VALIDATION,
      ],
    };

    modalityData.instances.forEach(instance => {
      const cacheKey = generateCacheKey(
        instance,
        twinId,
        PLANNING_STATES.VALIDATE
      );

      if (!digitalTwinSurgeryCache.hasView(cacheKey)) {
        return;
      }

      const cachedData = digitalTwinSurgeryCache.getView(cacheKey);

      if (Array.isArray(cachedData)) {
        const filteredViews = cachedData.filter(view => {
          const isAllowed = allowedViews[selectedModality]?.includes(
            view.viewName
          );
          return isAllowed;
        });

        filteredViews.forEach((view, index) => {
          views.push({
            key: `${cacheKey}_${
              view.viewName || index
            }_${validationSessionVersion}`,
            viewData: view,
            instance,
            viewName: view.viewName,
            index,
          });
        });
      } else {
        const isAllowed = allowedViews[selectedModality]?.includes(
          cachedData.viewName
        );
        if (isAllowed) {
          views.push({
            key: `${cacheKey}_${validationSessionVersion}`,
            viewData: cachedData,
            instance,
            viewName: cachedData.viewName,
            index: 0,
          });
        }
      }
    });

    return views;
  }, [
    selectedTwin,
    selectedModality,
    areViewsReady,
    groupedThumbnailsStandard,
    twinId,
    digitalTwinSurgeryCache,
    validationSessionVersion,
  ]);

  const handleSelectModality = useCallback((_index, modality) => {
    setSelectedModality(modality);
  }, []);

  const calculateLabelPosition = useCallback((normalizedY, viewId) => {
    const remoteViewerElement = document.querySelector(
      `[data-view-id="${viewId}"]`
    );

    if (!remoteViewerElement) {
      return {
        x: window.innerWidth - 280,
        y: normalizedY * window.innerHeight,
      };
    }

    const imageViewerElement = remoteViewerElement.closest(".image-viewer");

    if (!imageViewerElement) {
      return {
        x: window.innerWidth - 280,
        y: normalizedY * window.innerHeight,
      };
    }

    const bounds = imageViewerElement.getBoundingClientRect();
    const LABEL_WIDTH = 250;
    const MARGIN = 10;

    return {
      x: bounds.right - LABEL_WIDTH - MARGIN,
      y: bounds.top + normalizedY * bounds.height,
    };
  }, []);

  const handleDrrValidationPointAdded = useCallback(
    event => {
      const eventData = event.detail;

      if (!eventData || eventData.length === 0) {
        return;
      }

      const payload = eventData[0];
      const { pointIndex, normalizedPosition, color } = payload;

      const newPoints = [];
      const colorString = color
        ? `rgb(${Math.round(color[0] * 255)}, ${Math.round(
            color[1] * 255
          )}, ${Math.round(color[2] * 255)})`
        : "#ff0000";

      currentModalityViews.forEach(view => {
        if (view.viewData?.viewId) {
          const position = calculateLabelPosition(
            normalizedPosition?.y || 0.5,
            view.viewData.viewId
          );

          newPoints.push({
            pointIndex,
            viewId: view.viewData.viewId,
            viewType: view.viewName?.includes("Lateral")
              ? "LATERAL"
              : "FRONTAL",
            position,
            normalizedPosition: normalizedPosition || { x: 0.5, y: 0.5 },
            color: colorString,
            uniqueId: null,
            labelOffset: { x: 0, y: 0 },
          });
        }
      });

      setValidationPoints(prev => [...prev, ...newPoints]);
    },
    [calculateLabelPosition, currentModalityViews]
  );

  const handleCentroidChange = useCallback(
    async (pointIndex, uniqueId) => {
      try {
        if (!renderClient) {
          return;
        }

        // Get all unique point indices (not duplicates across views)
        const uniquePointIndices = [
          ...new Set(validationPoints.map(p => p.pointIndex)),
        ];

        // Check if this uniqueId is already assigned to other points (dedupe indices)
        const duplicatePointIndices = [
          ...new Set(
            validationPoints
              .filter(
                p => p.uniqueId === uniqueId && p.pointIndex !== pointIndex
              )
              .map(p => p.pointIndex)
          ),
        ];

        // Prepare updates array for batch processing
        const updates = [];

        // Track currently assigned unique IDs (unique point indexes only)
        const assignedPointIndicesBefore = new Set(
          validationPoints
            .filter(p => p.uniqueId !== null && p.uniqueId !== undefined)
            .map(p => p.pointIndex)
        );

        // 1. If duplicate exists, unselect the other point
        if (duplicatePointIndices.length > 0) {
          duplicatePointIndices.forEach(duplicateIndex => {
            updates.push({
              pointIndex: duplicateIndex,
              uniqueId: null,
            });
          });
        }

        // 2. Assign the selected uniqueId to current point
        updates.push({ pointIndex, uniqueId });

        // 3. Auto-fill unassigned points if this is the first assignment
        const shouldAutoFill =
          assignedPointIndicesBefore.size === 0 &&
          uniquePointIndices.length > 1;

        if (shouldAutoFill) {
          const sortedPointIndices = [...uniquePointIndices].sort((a, b) => {
            const pointA = validationPoints.find(p => p.pointIndex === a);
            const pointB = validationPoints.find(p => p.pointIndex === b);
            const yA = pointA?.normalizedPosition?.y ?? 0;
            const yB = pointB?.normalizedPosition?.y ?? 0;
            return yA - yB;
          });
          const selectedOrderIndex = sortedPointIndices.indexOf(pointIndex);

          let nextForwardId = uniqueId;
          let nextBackwardId = uniqueId;

          // Backward (upward): iterate from closest to selected outward so each
          // successive point gets the next-higher vertebra in the correct order.
          const backwardPoints = sortedPointIndices
            .slice(0, selectedOrderIndex)
            .reverse();
          backwardPoints.forEach(idx => {
            nextBackwardId = getPreviousVertebraInOrder(nextBackwardId);
            let assignedId = nextBackwardId;
            if (!assignedId) {
              nextForwardId = getNextVertebraInOrder(nextForwardId);
              assignedId = nextForwardId;
            }
            if (assignedId) {
              updates.push({ pointIndex: idx, uniqueId: assignedId });
            }
          });

          // Forward (downward): iterate from closest to selected outward so each
          // successive point gets the next-lower vertebra in the correct order.
          const forwardPoints = sortedPointIndices.slice(
            selectedOrderIndex + 1
          );
          forwardPoints.forEach(idx => {
            nextForwardId = getNextVertebraInOrder(nextForwardId);
            let assignedId = nextForwardId;
            if (!assignedId) {
              nextBackwardId = getPreviousVertebraInOrder(nextBackwardId);
              assignedId = nextBackwardId;
            }
            if (assignedId) {
              updates.push({ pointIndex: idx, uniqueId: assignedId });
            }
          });
        }

        // Execute all backend updates
        for (const update of updates) {
          await renderClient.setDrrPointId(
            PLANNING_STATES.VALIDATE,
            update.pointIndex,
            update.uniqueId
          );
        }

        // Update frontend state for all affected points
        setValidationPoints(prev => {
          const updatesMap = new Map(
            updates.map(u => [u.pointIndex, u.uniqueId])
          );
          return prev.map(point => {
            if (updatesMap.has(point.pointIndex)) {
              return { ...point, uniqueId: updatesMap.get(point.pointIndex) };
            }
            return point;
          });
        });
      } catch (error) {
        // Error is logged, but not to console
      }
    },
    [
      renderClient,
      validationPoints,
      getNextVertebraInOrder,
      getPreviousVertebraInOrder,
    ]
  );

  const rebuildValidationPointsFromState = useCallback(
    pointsState => {
      if (!pointsState || pointsState.length === 0) {
        setValidationPoints([]);
        return;
      }

      setValidationPoints(prevPoints => {
        const offsetMap = new Map();
        prevPoints.forEach(point => {
          const key = `${point.pointIndex}-${point.viewId}`;
          offsetMap.set(key, point.labelOffset || { x: 0, y: 0 });
        });

        const rebuiltPoints = [];

        pointsState.forEach(point => {
          const { index, uniqueId, views, color } = point;

          const colorString = color
            ? `rgb(${Math.round(color[0] * 255)}, ${Math.round(
                color[1] * 255
              )}, ${Math.round(color[2] * 255)})`
            : "#ff0000";

          if (Array.isArray(views) && views.length > 0) {
            views.forEach(viewData => {
              const { viewId, normalized, viewType } = viewData;

              if (normalized && normalized.y !== undefined) {
                const basePosition = calculateLabelPosition(
                  normalized.y,
                  viewId
                );
                const key = `${index}-${viewId}`;
                const offset = offsetMap.get(key) || { x: 0, y: 0 };

                rebuiltPoints.push({
                  pointIndex: index,
                  viewId,
                  viewType: viewType || "UNKNOWN",
                  position: {
                    x: basePosition.x + offset.x,
                    y: basePosition.y + offset.y,
                  },
                  normalizedPosition: normalized,
                  color: colorString,
                  uniqueId,
                  labelOffset: offset,
                });
              }
            });
          } else if (currentModalityViews.length > 0) {
            currentModalityViews.forEach(view => {
              if (view.viewData?.viewId) {
                const fallbackPosition = calculateLabelPosition(
                  0.5,
                  view.viewData.viewId
                );
                const key = `${index}-${view.viewData.viewId}`;
                const offset = offsetMap.get(key) || { x: 0, y: 0 };

                rebuiltPoints.push({
                  pointIndex: index,
                  viewId: view.viewData.viewId,
                  viewType: view.viewName?.includes("Lateral")
                    ? "LATERAL"
                    : "FRONTAL",
                  position: {
                    x: fallbackPosition.x + offset.x,
                    y: fallbackPosition.y + offset.y,
                  },
                  normalizedPosition: { x: 0.5, y: 0.5 },
                  color: colorString,
                  uniqueId,
                  labelOffset: offset,
                });
              }
            });
          }
        });

        return rebuiltPoints;
      });
    },
    [calculateLabelPosition, currentModalityViews]
  );

  const refreshDrrValidationPoints = useCallback(async () => {
    if (!renderClient) {
      return;
    }

    try {
      const pointsState = await renderClient.getDrrPointsState(
        PLANNING_STATES.VALIDATE
      );
      rebuildValidationPointsFromState(pointsState);
    } catch (error) {
      // Error is logged, but not to console
    }
  }, [renderClient, rebuildValidationPointsFromState]);

  const pendingRefreshRef = useRef(false);

  useEffect(() => {
    if (isDxMode) {
      return;
    }

    const activeViewIds = new Set(
      currentModalityViews
        .map(view => view.viewData?.viewId)
        .filter(viewId => viewId !== undefined && viewId !== null)
        .map(viewId => String(viewId))
    );

    if (activeViewIds.size === 0) {
      return;
    }

    const extractViewIds = detail => {
      if (!detail) return [];

      if (Array.isArray(detail)) {
        return detail
          .map(item => {
            if (item == null) return null;
            if (typeof item === "object") {
              return item.view ?? item.viewId ?? item.id ?? null;
            }
            return item;
          })
          .filter(viewId => viewId !== null && viewId !== undefined)
          .map(viewId => String(viewId));
      }

      if (typeof detail === "object") {
        const viewId = detail.view ?? detail.viewId ?? detail.id;
        return viewId !== undefined && viewId !== null ? [String(viewId)] : [];
      }

      return [String(detail)];
    };

    const handleUpdateViews = event => {
      const eventViewIds = extractViewIds(event.detail);
      const isRelevant = eventViewIds.some(viewId => activeViewIds.has(viewId));

      if (!isRelevant) {
        return;
      }

      if (pendingRefreshRef.current) {
        return;
      }

      pendingRefreshRef.current = true;
      Promise.resolve(refreshDrrValidationPoints()).finally(() => {
        pendingRefreshRef.current = false;
      });
    };

    window.addEventListener(EventsEnum.UPDATE_VIEWS, handleUpdateViews);
    return () => {
      window.removeEventListener(EventsEnum.UPDATE_VIEWS, handleUpdateViews);
    };
  }, [isDxMode, currentModalityViews, refreshDrrValidationPoints]);

  const handlePointDelete = useCallback(
    async pointIndex => {
      try {
        if (!renderClient) {
          return;
        }

        await renderClient.removeDrrPoint(PLANNING_STATES.VALIDATE, pointIndex);
        await refreshDrrValidationPoints();
      } catch (error) {
        // Error is logged, but not to console
      }
    },
    [renderClient, refreshDrrValidationPoints]
  );

  const handleLabelMove = useCallback(
    (pointIndex, viewId, newPosition) => {
      setValidationPoints(prev =>
        prev.map(point => {
          if (point.pointIndex === pointIndex && point.viewId === viewId) {
            const normalizedY = point.normalizedPosition?.y ?? 0.5;
            const basePosition = calculateLabelPosition(normalizedY, viewId);
            const offset = {
              x: newPosition.x - basePosition.x,
              y: newPosition.y - basePosition.y,
            };

            return {
              ...point,
              position: newPosition,
              labelOffset: offset,
            };
          }
          return point;
        })
      );
    },
    [calculateLabelPosition]
  );

  const handleLockToggle = useCallback(
    async (viewId, isLocked) => {
      try {
        if (!renderClient) {
          return;
        }

        await renderClient.setValidationLock(
          PLANNING_STATES.VALIDATE,
          viewId,
          isLocked
        );
      } catch (error) {
        // Error is logged, but not to console
        throw error;
      }
    },
    [renderClient]
  );

  const handleRerunLandmarking = useCallback(
    async metadata => {
      if (!instances || isRerunning) return;

      // Set orchestration flag to prevent effect from interfering
      isRerunOrchestrating.current = true;
      setIsRerunning(true);
      setRerunProgress({ completed: 0, total: 0 });
      setAreViewsReady(false);
      setIsLoading(true);

      try {
        // Step 1: Rerun landmarking on backend
        const sagittalInstanceId =
          instances.sagittal_dx_instance_id?._id ||
          instances.sagittal_dx_instance_id;
        const coronalInstanceId =
          instances.coronal_dx_instance_id?._id ||
          instances.coronal_dx_instance_id;

        const targetInstanceIds = [
          sagittalInstanceId,
          coronalInstanceId,
        ].filter(Boolean);

        if (targetInstanceIds.length === 0) {
          throw new Error("No DX instances found to rerun landmarking");
        }

        setRerunProgress({ completed: 0, total: targetInstanceIds.length });

        const rerunWithProgress = async instanceId => {
          try {
            await rerunInstanceLandmarking(instanceId, metadata);
          } finally {
            setRerunProgress(prev => {
              if (!prev.total) return prev;
              const nextCompleted = Math.min(prev.completed + 1, prev.total);
              if (nextCompleted === prev.completed) return prev;
              return { ...prev, completed: nextCompleted };
            });
          }
        };

        await Promise.all(targetInstanceIds.map(rerunWithProgress));

        // Step 2: Clear cache
        if (digitalTwinSurgeryCache) {
          digitalTwinSurgeryCache.clear();
        }

        // Step 3: Fetch fresh data from server and get it returned directly
        const freshConstructedImageData = await validationRefresh();

        if (!freshConstructedImageData) {
          throw new Error("Failed to fetch fresh data after rerun");
        }

        // Step 4: Load surgery with fresh data
        if (!renderClient || allInstances.length === 0) {
          throw new Error("Missing required data for surgery load");
        }

        const response = await renderClient.LoadSpinesimViewStage(
          PLANNING_STATES.VALIDATE,
          JSON.stringify(freshConstructedImageData),
          PLANNING_STATES.VALIDATE,
          true
        );

        const surgeryResult = JSON.parse(response);

        // Step 5: Cache the results
        const dxViews = surgeryResult.filter(
          view =>
            view.viewName === MedicalImagesType.LATERAL_VALIDATION_DX ||
            view.viewName === MedicalImagesType.FRONT_VALIDATION_DX
        );

        const sagittalInstance = allInstances.find(
          inst => inst._id === sagittalInstanceId
        );
        const coronalInstance = allInstances.find(
          inst => inst._id === coronalInstanceId
        );

        if (sagittalInstance) {
          const sagittalCacheKey = generateCacheKey(
            sagittalInstance,
            twinId,
            PLANNING_STATES.VALIDATE
          );
          const sagittalViews = dxViews.filter(view =>
            view.viewName.includes("Lateral")
          );
          if (sagittalViews.length > 0) {
            digitalTwinSurgeryCache.setView(sagittalCacheKey, sagittalViews);
          }
        }

        if (coronalInstance) {
          const coronalCacheKey = generateCacheKey(
            coronalInstance,
            twinId,
            PLANNING_STATES.VALIDATE
          );
          const coronalViews = dxViews.filter(view =>
            view.viewName.includes("Front")
          );
          if (coronalViews.length > 0) {
            digitalTwinSurgeryCache.setView(coronalCacheKey, coronalViews);
          }
        }

        // Step 6: Mark views as ready
        setAreViewsReady(true);
        triggerCheckReload();
      } catch (error) {
        setAreViewsReady(false);
      } finally {
        setIsRerunning(false);
        setIsLoading(false);
        setRerunProgress({ completed: 0, total: 0 });
        // Release orchestration lock
        isRerunOrchestrating.current = false;
      }
    },
    [
      instances,
      isRerunning,
      validationRefresh,
      digitalTwinSurgeryCache,
      allInstances,
      twinId,
      renderClient,
      triggerCheckReload,
    ]
  );

  const renderDxGrid = (views, renderClient) => {
    const gridClass = views.length === 1 ? "single-view" : "dual-view";

    return (
      <div className={`Validation-dx-grid ${gridClass}`}>
        {views.map(view => (
          <ImageViewer
            key={`image-container-${view.key}`}
            viewData={view.viewData}
            instance={view.instance}
            onContentLoad={() => {}}
            onCameraClick={() => {}}
            onBrightnessClick={() => {}}
            onLockToggle={isLocked =>
              handleLockToggle(view.viewData.viewId, isLocked)
            }
            hasUndoRedo
            step={PLANNING_STATES.VALIDATE}
            viewType={view.viewName}
            remoteRenderClient={renderClient}
            viewId={view.viewData.viewId}
            planId={PLANNING_STATES.VALIDATE}
            instanceId={view.instance?._id}
            displaySettingsViewType={getDisplaySettingsViewType(view.viewName)}
            persistedDisplaySettings={
              view.instance?.display_settings?.[
                getDisplaySettingsViewType(view.viewName)
              ]
            }
          >
            <RemoteViewer
              remoteRenderClient={renderClient}
              viewData={view.viewData}
              sliderWidth="80%"
            />
          </ImageViewer>
        ))}
      </div>
    );
  };

  const renderOtherModalityGrid = (views, modality, renderClient) => {
    return (
      <div className={`Validation-${modality.toLowerCase()}-grid`}>
        {views.map(view => (
          <ImageViewer
            key={`image-container-${view.key}`}
            viewData={view.viewData}
            instance={view.instance}
            onContentLoad={() => {}}
            onCameraClick={() => {}}
            onBrightnessClick={() => {}}
            step={PLANNING_STATES.VALIDATE}
            viewType={view.viewName}
            remoteRenderClient={renderClient}
            viewId={view.viewData.viewId}
            planId={PLANNING_STATES.VALIDATE}
            instanceId={view.instance?._id}
            displaySettingsViewType={getDisplaySettingsViewType(view.viewName)}
            persistedDisplaySettings={
              view.instance?.display_settings?.[
                getDisplaySettingsViewType(view.viewName)
              ]
            }
          >
            <RemoteViewer
              remoteRenderClient={renderClient}
              viewData={view.viewData}
              sliderWidth="80%"
            />
          </ImageViewer>
        ))}
      </div>
    );
  };

  const renderModalityGrid = () => {
    if (!selectedModality) {
      return (
        <div className="no-view-message">Select a modality to view details</div>
      );
    }

    if (isLoading || isRerunning || !areViewsReady) {
      return (
        <ViewLoader
          isLoading={true}
          message={
            isRerunning
              ? "Rerunning AI model and updating annotations..."
              : "Loading validation data..."
          }
          currentStep={isRerunning ? rerunProgress.completed : undefined}
          totalSteps={isRerunning ? rerunProgress.total : undefined}
        >
          {/* Empty children since we're still loading */}
        </ViewLoader>
      );
    }

    const views = currentModalityViews;

    if (views.length === 0) {
      return (
        <div className="no-view-message">
          No {selectedModality} views available for validation
        </div>
      );
    }

    switch (selectedModality) {
      case SPINESIM_MODALITIES.DX:
        return renderDxGrid(views, renderClient);
      case SPINESIM_MODALITIES.CT:
      case SPINESIM_MODALITIES.MR:
        return renderOtherModalityGrid(views, selectedModality, renderClient);
      default:
        return renderOtherModalityGrid(views, selectedModality, renderClient);
    }
  };

  const renderTools = () => {
    if (!isDxMode) {
      return null;
    }

    return (
      <div
        className={`Validation-tools ${
          isLoading || isRerunning || !areViewsReady ? "disabled" : ""
        }`}
        style={
          isLoading || isRerunning || !areViewsReady
            ? { pointerEvents: "none", opacity: 0.5 }
            : undefined
        }
      >
        <ToolBar
          step={ROUTE_TYPES.VALIDATION}
          currentValidationRegion={currentValidationRegion}
        />
      </div>
    );
  };

  const handleRegionChange = useCallback(region => {
    setCurrentValidationRegion(region);
  }, []);

  // Clear CHECK cache before navigating to force fresh reload
  const handleCheckReloadRequested = useCallback(() => {
    const twinId = routeState.params.twinId;
    if (allInstances.length > 0 && twinId && digitalTwinSurgeryCache) {
      allInstances.forEach(instance => {
        const checkCacheKey = generateCacheKey(
          instance,
          twinId,
          PLANNING_STATES.CHECK
        );
        if (digitalTwinSurgeryCache.hasView(checkCacheKey)) {
          digitalTwinSurgeryCache.removeView(checkCacheKey);
        }
      });
    }
    triggerCheckReload();
  }, [
    allInstances,
    routeState.params.twinId,
    digitalTwinSurgeryCache,
    triggerCheckReload,
  ]);

  useEffect(() => {
    showOnlyRightSidebar(
      <ValidationToolsWidget
        instances={instances}
        isLoadingViews={isLoading}
        refreshData={validationRefresh}
        onCheckReloadRequested={handleCheckReloadRequested}
        validationMode={validationMode}
        onRegionChange={handleRegionChange}
        allDrrLabelsAssigned={allDrrLabelsAssigned}
        onRerunLandmarking={handleRerunLandmarking}
        viewReloadToken={validationSessionVersion}
        disabled={isLoading || isRerunning || !areViewsReady}
        annotationsMap={dataBundle.annotations}
      />,
      SidebarSize.LARGE
    );
  }, [
    instances,
    isLoading,
    showOnlyRightSidebar,
    validationMode,
    handleRegionChange,
    allDrrLabelsAssigned,
    handleRerunLandmarking,
    validationSessionVersion,
    isRerunning,
    areViewsReady,
    validationRefresh,
    triggerCheckReload,
    dataBundle.annotations,
  ]);

  useEffect(() => {
    if (groupedThumbnailsStandard?.length > 0 && !selectedModality) {
      const firstModality = groupedThumbnailsStandard[0].modality;
      setSelectedModality(firstModality);
    }
  }, [groupedThumbnailsStandard, selectedModality]);

  useEffect(() => {
    window.addEventListener(
      EventsEnum.DRR_VALIDATION_POINT_ADDED,
      handleDrrValidationPointAdded
    );

    return () => {
      window.removeEventListener(
        EventsEnum.DRR_VALIDATION_POINT_ADDED,
        handleDrrValidationPointAdded
      );
    };
  }, [handleDrrValidationPointAdded]);

  useEffect(() => {
    if (!areViewsReady || !renderClient) {
      return;
    }

    const restorePointsState = async () => {
      try {
        const pointsState = await renderClient.getDrrPointsState(
          PLANNING_STATES.VALIDATE
        );

        if (!pointsState || pointsState.length === 0) {
          setValidationPoints([]);
          return;
        }

        rebuildValidationPointsFromState(pointsState);
      } catch (error) {
        // Error is logged, but not to console
      }
    };

    restorePointsState();
  }, [areViewsReady, renderClient, rebuildValidationPointsFromState]);

  useEffect(() => {}, [
    dataBundle,
    constructedImageData,
    instances,
    allInstances.length,
    isLoading,
  ]);

  return (
    <div className="Visualization-region-container">
      <div className="Visualization-region-header">
        <div className="Visualization-region-title App-title-widgets">
          {`Digital Twin Validation: ${selectedTwinName}`}
        </div>
      </div>
      <div className="Visualization-region-content">
        <div className="Visualization-region-tab">
          <ThumbnailTab
            thumbnails={groupedThumbnailsStandard}
            selectedThumbnail={selectedModality}
            onSelectThumbnail={handleSelectModality}
          />
          {renderClient && isOverlayVisible && isDxMode && (
            <div className="Validation-visibility-overlay-tools-wrapper">
              <ValidationOverlayTools
                remoteRenderClient={renderClient}
                viewReloadToken={validationSessionVersion}
                disabled={isLoading || isRerunning || !areViewsReady}
              />
            </div>
          )}
        </div>
        <div className="Validation-region-views">{renderModalityGrid()}</div>
        {isDxMode && (
          <div className="Validation-region-tools-wrapper">{renderTools()}</div>
        )}
      </div>

      {/* Render validation point labels */}
      {validationPoints.map(point => {
        const isCurrentView = currentModalityViews.some(
          v => v.viewData?.viewId === point.viewId
        );

        if (!isCurrentView) return null;

        return (
          <ValidationPointLabel
            key={`${point.viewId}-${point.pointIndex}`}
            pointIndex={point.pointIndex}
            viewId={point.viewId}
            position={point.position}
            color={point.color}
            centroids={centroidOptions}
            selectedCentroid={point.uniqueId}
            onCentroidChange={handleCentroidChange}
            onDelete={handlePointDelete}
            onPositionChange={handleLabelMove}
          />
        );
      })}
    </div>
  );
};

export default Validation;
